import { useCallback, useMemo, useRef, useState } from "react";

type UseUndoRedoReturn<T> = Readonly<{
  capture: (currentState: T[]) => void;
  undo: (currentState: T[]) => T[] | null;
  redo: (currentState: T[]) => T[] | null;
  canUndo: boolean;
  canRedo: boolean;
  reset: () => void;
}>;

export const useUndoRedo = <T>(maxHistory = 50): UseUndoRedoReturn<T> => {
  const undoStackRef = useRef<T[][]>([]);
  const redoStackRef = useRef<T[][]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const capture = useCallback(
    (currentState: T[]) => {
      undoStackRef.current = [
        ...undoStackRef.current.slice(-(maxHistory - 1)),
        currentState,
      ];
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
    },
    [maxHistory]
  );

  const undo = useCallback((currentState: T[]): T[] | null => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return null;

    const previous = stack[stack.length - 1];
    if (!previous) return null;
    undoStackRef.current = stack.slice(0, -1);
    redoStackRef.current = [...redoStackRef.current, currentState];
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    return previous;
  }, []);

  const redo = useCallback((currentState: T[]): T[] | null => {
    const stack = redoStackRef.current;
    if (stack.length === 0) return null;

    const next = stack[stack.length - 1];
    if (!next) return null;
    redoStackRef.current = stack.slice(0, -1);
    undoStackRef.current = [...undoStackRef.current, currentState];
    setCanRedo(redoStackRef.current.length > 0);
    setCanUndo(true);
    return next;
  }, []);

  const reset = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return useMemo(
    () => ({ capture, undo, redo, canUndo, canRedo, reset }),
    [capture, undo, redo, canUndo, canRedo, reset]
  );
};
